import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IonCol, IonContent, IonPage } from "@ionic/react";
import { RouteComponentProps } from "react-router";
import Header from "../../../components/Headers/Header";
import { getEventById } from "../../../utilities/events/Event";
import ErrorAlert from "../../../components/Errors/ErrorAlert";
import EntrySectionCard from "../../../components/Cards/EntrySectionCard";
import EntryPageHeader from "../../../components/Entry/EntryForm/EntryPageHeader";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { Event, EventEntry } from "../../../models";
import { NewEntryPageSkeleton } from "../../../components/Skeletons/EntrySectionSkeletons";
import { CreateEntryInterface, createNewEventEntry } from "../../../utilities/eventEntry/CreateEventEntryUtils";
import { copyEventEntriesToEvent } from "../../../utilities/eventEntry/CopyEventEntryUtils";
import { PersonContext } from "../../../context/PersonContext";
import { getEventEntriesByEventIdPersonId } from "../../../utilities/eventEntry/EventEntry";
import { removeEventEntryData } from "../../../utilities/eventEntry/RemoveEventEntryData";
import { sortEventEntriesByCreatedDate } from "../../../utilities/eventEntry/SortEventEntry";
import { updateExistingEventEntry } from "../../../utilities/eventEntry/UpdateEventEntryUtils";
import CopyPreviousEntryModal, { userHasCopyableEntries } from "../../../components/Modals/CopyPreviousEntryModal";
import DeleteConfirmationModal from "../../../components/Modals/DeleteConfirmationModal";
import BasicEntryInfoCard from "../../../components/Entry/BasicEntryInfoCard";
import { EntrySummaryNavigateTarget } from "../../../components/Entry/EntryForm/EntrySummarySection";
import { ExpandedCardStates, NEW_ENTRY_SCROLL } from "../../../components/Entry/NewEntry/newEntryTypes";
import UnsavedChangesModal from "../../../components/Entry/NewEntry/UnsavedChangesModal";
import CompactEntryGrid from "../../../components/Entry/NewEntry/CompactEntryGrid";
import {
    collapseFromBlurredArea as dismissExpandedFromBlur,
    PageSectionBlurBackdrop,
    ParentSectionFocusWrap,
} from "../../../components/Entry/NewEntry/parentSectionFocus";
import { formatPendingEntrySubtitle, getExpandedSectionInfo } from "../../../components/Entry/NewEntry/entryHeaderUtils";

interface EventNewEntryPageProps extends RouteComponentProps<{ id: string }> {}

/**
 * New Entry redesign orchestrator (`/index/event/:id/new-entry-redesign`).
 * Owns route param, event/entries load, which section is expanded, and CRUD.
 * Presentational grid, add actions, unsaved-changes UI, and layout measurement live under `src/components/Entry/NewEntry/`.
 */
const EventNewEntryPage: React.FC<EventNewEntryPageProps> = ({ match }) => {
    const user = useContext(PersonContext);

    // State needed for loading the page
    const [expandedCard, setExpandedCard] = useState<ExpandedCardStates>("header");
    const [currentEvent, setCurrentEvent] = useState<Event>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // State for tracking changes on the page
    const [currentEntries, setCurrentEntries] = useState<EventEntry[] | undefined | null>();
    /** Unsaved "New Horse" draft cards (added via Add New Entry / copy flow). */
    const [extraNewEntryCount, setExtraNewEntryCount] = useState(0);

    // State for tracking saving status per entry
    const [savingEntryId, setSavingEntryId] = useState<string | null | undefined>(undefined); // undefined = not saving, null = saving new entry, string = saving existing entry ID

    // State for tracking pending entry subtitles (for immediate UI updates)
    const [pendingEntrySubtitles, setPendingEntrySubtitles] = useState<Map<string | null, string>>(new Map());

    // State for delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState<EventEntry | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [exitingEntryId, setExitingEntryId] = useState<string | null>(null);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const [showCopyEntryModal, setShowCopyEntryModal] = useState(false);
    const [canCopyPreviousEntry, setCanCopyPreviousEntry] = useState(false);
    const [copyingEntryCount, setCopyingEntryCount] = useState(0);
    /** Incremented to tell the currently expanded card to submit (from the unsaved-changes modal). */
    const [saveChangesRequestKey, setSaveChangesRequestKey] = useState(0);
    const [hasUnsavedChangesInExpandedCard, setHasUnsavedChangesInExpandedCard] = useState(false);

    /** From Entry Summary → Waivers: remount waiver accordions; optionally expand only one horse. */
    const [waiversAccordionFocusKey, setWaiversAccordionFocusKey] = useState(0);
    const [waiversExpandOnlyEntryId, setWaiversExpandOnlyEntryId] = useState<string | undefined>(undefined);

    /** From Entry Summary → Stabling: remount entry subcards; optionally expand only one horse. */
    const [stablingAccordionFocusKey, setStablingAccordionFocusKey] = useState(0);
    const [stablingExpandOnlyEntryId, setStablingExpandOnlyEntryId] = useState<string | undefined>(undefined);
    /** When set, expanding an entry from summary scrolls to Enter Classes instead of the card top. */
    const pendingEntryScrollFocusRef = useRef<"classes" | null>(null);

    useEffect(() => {
        async function loadData(eventId: string) {
            // Set the loading spinner when
            setIsLoading(true);
            // First load the event and set it in state
            await getEvent(eventId);
            // Next, load any previous entries submitted by this user for this event
            await getEventEntriesForCurrentUser(eventId);
            // Stop the loading at this point - load other data in the background
            setIsLoading(false);
        }
        async function getEvent(eventId: string) {
            const queryResult = await getEventById(eventId);
            if (queryResult.isSuccess) {
                setCurrentEvent(queryResult.result);
            } else {
                setError("Sorry, a problem occurred. Please go back and try again.");
            }
        }

        async function getEventEntriesForCurrentUser(eventId: string) {
            if (user) {
                const queryResult = await getEventEntriesByEventIdPersonId(eventId, user.id);
                if (queryResult.isSuccess) {
                    const entries: EventEntry[] = queryResult.result;
                    const sorted = sortEventEntriesByCreatedDate(entries);
                    setCurrentEntries(sorted);
                    // Multiple entries: collapse Event Info so the compact minified entry grid has room
                    if (sorted && sorted.length > 1) {
                        setExpandedCard(undefined);
                    }
                }
            } else {
                setError("Sorry, a problem occurred (no user found). Please go back and try again.");
            }
        }

        // if the page has loaded and no event has been loaded yet (aka inital page load), then load all page data
        if (match.params.id && !currentEvent) loadData(match.params.id);
    }, [match.params.id]);

    // Enable Copy Previous Entry only when this user has copyable entries on other events.
    useEffect(() => {
        if (!user?.id || !currentEvent?.id) {
            setCanCopyPreviousEntry(false);
            return;
        }

        let cancelled = false;
        void userHasCopyableEntries(currentEvent.id, user.id, currentEntries ?? []).then((hasCopyable) => {
            if (!cancelled) {
                setCanCopyPreviousEntry(hasCopyable);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [user?.id, currentEvent?.id, currentEntries]);

    /** Expand one section or collapse the current one. Clears the dirty flag so a leftover modal doesn't fire. */
    const handleCollapseOrExpand = (card: ExpandedCardStates) => (isExpand: boolean) => {
        setHasUnsavedChangesInExpandedCard(false);
        if (isExpand) setExpandedCard(card);
        else setExpandedCard(undefined);
    };

    /** Delay so expand animation can start before the card is scrolled into view. */
    const scrollToNewEntryAnchor = useCallback((elementId: string) => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 200);
        });
    }, []);

    const scrollToEntryClassesSection = useCallback((oneBasedIndex: number) => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                const entryEl =
                    document.getElementById(NEW_ENTRY_SCROLL.entry(oneBasedIndex)) ||
                    (oneBasedIndex === 1 ? document.getElementById(NEW_ENTRY_SCROLL.entryNew) : null);
                const classesEl = entryEl?.querySelector("[data-entry-classes-section]") as HTMLElement | null;
                (classesEl || entryEl)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 250);
        });
    }, []);

    /** Save/Continue closes the current card without auto-opening the next one. */
    const collapseExpandedCard = useCallback(() => {
        setHasUnsavedChangesInExpandedCard(false);
        setExpandedCard(undefined);
    }, []);

    /**
     * Show delete confirmation modal for entry deletion
     * @param currentEventEntry
     */
    const handleDeleteSavedEntry = (currentEventEntry: EventEntry) => {
        if (isDeleting || exitingEntryId) return;
        setError("");
        setEntryToDelete(currentEventEntry);
        setShowDeleteModal(true);
    };

    /**
     * Confirm and execute entry deletion
     */
    const confirmDeleteEntry = async () => {
        if (!entryToDelete) return;

        setError("");
        setIsDeleting(true);
        // Close modal so the entry card can show the deleting status loader
        setShowDeleteModal(false);
        // Collapse immediately so blur clears during delete (same pattern as save)
        setHasUnsavedChangesInExpandedCard(false);
        setExpandedCard(undefined);

        const deleteResult = await removeEventEntryData(entryToDelete);

        if (deleteResult.isSuccess) {
            // Keep the card mounted and animate it out; remove from state after exit completes
            setExitingEntryId(entryToDelete.id);
        } else {
            console.error("Error: ", deleteResult.message);
            setError(deleteResult.message || "Failed to delete entry. Please try again.");
            setIsDeleting(false);
        }
    };

    /**
     * Finish removal after the exit animation collapses the card height
     */
    const handleEntryExitComplete = useCallback((entryId: string) => {
        setCurrentEntries((prevValue) => {
            const updatedValue = prevValue ? prevValue.filter((eventEntry) => eventEntry.id !== entryId) : [];
            return updatedValue;
        });
        setPendingEntrySubtitles((prev) => {
            const newMap = new Map(prev);
            newMap.delete(entryId);
            return newMap;
        });
        setExitingEntryId(null);
        setEntryToDelete(null);
        setIsDeleting(false);
    }, []);

    /**
     * Cancel entry deletion
     */
    const cancelDeleteEntry = () => {
        setShowDeleteModal(false);
        setEntryToDelete(null);
    };

    /**
     * Function for adding a new record to the DB for an entry
     * @param entryInfo
     */
    const saveInitialEntry = async (entryInfo: CreateEntryInterface | undefined) => {
        // First, clear out any old error messages
        setError("");

        // Check for any new issues
        // TODO - these errors are shown at very top of page. Is that the best place for them?
        if (!currentEvent) {
            setError("No event was found. Please try again.");
            return;
        }
        if (!entryInfo) {
            setError("No entry information was found. Please try again.");
            return;
        }

        // Update subtitle immediately with entry info
        const subtitle = formatPendingEntrySubtitle(entryInfo.currentHorse?.name, entryInfo.currentRider?.name, entryInfo.currentRider2?.name);
        setPendingEntrySubtitles((prev) => {
            const newMap = new Map(prev);
            newMap.set(null, subtitle); // null represents the new entry card
            return newMap;
        });

        // Set saving state and collapse the section
        setSavingEntryId(null); // null represents the new entry card
        setExpandedCard(undefined); // Collapse the section

        // Create a new entry
        const createResult = await createNewEventEntry(entryInfo);

        if (createResult.isSuccess) {
            const newEventEntry: EventEntry = createResult.result;

            // Add the new entry to the list of entries this user has created
            setCurrentEntries((prevValue) => {
                const updatedValue = prevValue ? prevValue.concat([newEventEntry]) : [newEventEntry];
                return updatedValue;
            });
        } else {
            console.error("Error: ", createResult.message);
            setError(createResult.message || "Failed to save entry. Please try again.");
        }

        // Clear saving state, consume the draft slot, and drop the pending new-entry subtitle.
        setSavingEntryId(undefined);
        setExtraNewEntryCount((count) => Math.max(0, count - 1));
        setPendingEntrySubtitles((prev) => {
            const newMap = new Map(prev);
            newMap.delete(null);
            return newMap;
        });
    };

    /**
     * Function for updating a record in the DB for an entry
     * @param entryInfo
     */
    const saveUpdatedEntry = async (currentEntry: EventEntry, entryInfo: CreateEntryInterface | undefined) => {
        // First, clear out any old error messages
        setError("");

        // Check for any new issues
        if (!currentEvent) {
            setError("No event was found. Please try again.");
            return;
        }
        if (!entryInfo) {
            setError("No entry information was found. Please try again.");
            return;
        }

        // Update subtitle immediately with entry info
        const entryId = currentEntry.id || null;
        const subtitle = formatPendingEntrySubtitle(entryInfo.currentHorse?.name, entryInfo.currentRider?.name, entryInfo.currentRider2?.name);
        setPendingEntrySubtitles((prev) => {
            const newMap = new Map(prev);
            newMap.set(entryId, subtitle);
            return newMap;
        });

        // Set saving state and collapse the section
        setSavingEntryId(entryId);
        setExpandedCard(undefined); // Collapse the section

        // Create a new entry
        const updateResult = await updateExistingEventEntry(currentEntry, entryInfo);

        if (updateResult.isSuccess) {
            const updatedEventEntry: EventEntry = updateResult.result;

            let updatedEntries: EventEntry[] = [];
            if (currentEntries && currentEntries.length > 0) {
                const updatedEntryIndex = currentEntries?.findIndex((e) => e.id === updatedEventEntry.id);
                updatedEntries = [...currentEntries.slice(0, updatedEntryIndex), updatedEventEntry, ...currentEntries.slice(updatedEntryIndex + 1)];
            } else {
                updatedEntries = [updatedEventEntry];
            }

            // Add the new entry to the list of entries this user has updated
            setCurrentEntries(updatedEntries);
        } else {
            console.error("Error: ", updateResult.message);
            setError(updateResult.message || "Failed to save entry. Please try again.");
        }

        // Clear saving state and pending subtitle
        setSavingEntryId(undefined);
        if (currentEntry.id) {
            setPendingEntrySubtitles((prev) => {
                const newMap = new Map(prev);
                newMap.delete(currentEntry.id || null);
                return newMap;
            });
        }
    };

    /** Adds an empty "New Horse" draft card; saving it consumes one slot. */
    const handleAddAnotherEntry = () => {
        setExtraNewEntryCount((count) => count + 1);
    };

    const handleCloseCopyEntryModal = () => {
        setShowCopyEntryModal(false);
    };

    const handleCopyEntries = async (sourceEntries: EventEntry[]) => {
        if (!currentEvent || !user || sourceEntries.length === 0) {
            return;
        }

        // Skeleton count matches source length until copy finishes and real cards replace them.
        setCopyingEntryCount(sourceEntries.length);
        setError("");

        try {
            const copyResult = await copyEventEntriesToEvent(currentEvent, sourceEntries, user.id, user.roles);

            if (!copyResult.isSuccess) {
                setError(copyResult.message || "Failed to copy entries. Please try again.");
                return;
            }

            const copiedEntries: EventEntry[] = copyResult.result ?? [];
            setCurrentEntries((prevValue) => (prevValue ? prevValue.concat(copiedEntries) : copiedEntries));
        } finally {
            setCopyingEntryCount(0);
        }
    };

    const clearWaiversExpandTarget = useCallback(() => {
        setWaiversExpandOnlyEntryId(undefined);
    }, []);

    const clearStablingExpandTarget = useCallback(() => {
        setStablingExpandOnlyEntryId(undefined);
    }, []);

    /** Click-outside / blurred-section dismiss: prompt to save when the open card is dirty. */
    const collapseFromBlurredArea = useCallback(() => {
        dismissExpandedFromBlur(expandedCard, hasUnsavedChangesInExpandedCard, collapseExpandedCard, () => setShowUnsavedChangesModal(true));
    }, [expandedCard, hasUnsavedChangesInExpandedCard, collapseExpandedCard]);

    const handleDismissUnsavedChangesModal = () => {
        setShowUnsavedChangesModal(false);
        collapseExpandedCard();
    };

    const handleCloseUnsavedChangesModal = () => {
        setShowUnsavedChangesModal(false);
    };

    /** Ask the expanded card to submit by bumping saveRequestKey. */
    const handleSaveChangesFromModal = () => {
        setShowUnsavedChangesModal(false);
        setSaveChangesRequestKey((currentKey) => currentKey + 1);
    };

    /** Discard an unsaved "New Horse" slot (not a persisted entry). */
    const handleDeleteDraft = (draftCardKey: ExpandedCardStates) => {
        setExtraNewEntryCount((count) => Math.max(0, count - 1));
        if (expandedCard === draftCardKey) {
            setExpandedCard(undefined);
        }
        setPendingEntrySubtitles((prev) => {
            const newMap = new Map(prev);
            newMap.delete(null);
            return newMap;
        });
    };

    const handleOpenCopyEntryModal = () => {
        if (Boolean(expandedCard) || copyingEntryCount > 0 || !canCopyPreviousEntry) return;
        setShowCopyEntryModal(true);
    };

    const expandedSectionInfo = getExpandedSectionInfo(expandedCard, currentEntries, pendingEntrySubtitles);
    const entryToDeleteIndex =
        entryToDelete && currentEntries ? currentEntries.findIndex((e) => e.id === entryToDelete.id) : -1;
    const entryToDeleteNumber = entryToDeleteIndex >= 0 ? entryToDeleteIndex + 1 : undefined;

    /** Entry Summary edit actions: expand the target section and scroll it into view. */
    const handleNavigateFromSummary = useCallback(
        (target: EntrySummaryNavigateTarget) => {
            if (target.section === "showFees") return;

            if (target.section === "header") {
                setExpandedCard("header");
                scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.header);
                return;
            }
            if (target.section === "stabling") {
                setExpandedCard("stabling");
                setStablingExpandOnlyEntryId(target.eventEntryId);
                if (target.eventEntryId) {
                    setStablingAccordionFocusKey((k) => k + 1);
                }
                scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.stabling);
                return;
            }
            if (target.section === "waivers") {
                setExpandedCard("waivers");
                setWaiversExpandOnlyEntryId(target.eventEntryId);
                setWaiversAccordionFocusKey((k) => k + 1);
                scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.waivers);
                return;
            }
            if (target.section === "entry") {
                const n = target.entryIndex + 1;
                pendingEntryScrollFocusRef.current = target.focus === "classes" ? "classes" : null;
                setExpandedCard(`entry${n}` as ExpandedCardStates);
                if (target.focus === "classes") {
                    scrollToEntryClassesSection(n);
                } else {
                    scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.entry(n));
                }
            }
        },
        [scrollToNewEntryAnchor, scrollToEntryClassesSection]
    );

    // Keep the expanded card in view after expand (same anchors Summary edit actions use).
    useEffect(() => {
        if (!expandedCard) return;

        if (expandedCard === "header") {
            scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.header);
            return;
        }
        if (expandedCard === "stabling") {
            scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.stabling);
            return;
        }
        if (expandedCard === "waivers") {
            scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.waivers);
            return;
        }
        if (expandedCard === "summary" || expandedCard === "review") {
            scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.summary);
            return;
        }
        if (expandedCard === "entry") {
            if (pendingEntryScrollFocusRef.current === "classes") {
                pendingEntryScrollFocusRef.current = null;
                scrollToEntryClassesSection(1);
                return;
            }
            scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.entryNew);
            return;
        }
        if (expandedCard.startsWith("entry")) {
            const n = Number.parseInt(expandedCard.replace("entry", ""), 10);
            if (Number.isFinite(n) && n > 0) {
                if (pendingEntryScrollFocusRef.current === "classes") {
                    pendingEntryScrollFocusRef.current = null;
                    scrollToEntryClassesSection(n);
                    return;
                }
                scrollToNewEntryAnchor(NEW_ENTRY_SCROLL.entry(n));
            }
        }
    }, [expandedCard, scrollToNewEntryAnchor, scrollToEntryClassesSection]);

    return (
        <IonPage className="bg-medium p-0 m-0">
            <Header />
            <IonContent className="p-0 m-0">
                <PageTitle title="Your Entries" newStyles={true} />
                {error && <ErrorAlert width="12" error={error} />}
                {isLoading ? (
                    <NewEntryPageSkeleton />
                ) : currentEvent ? (
                    <IonCol className="p-0 m-0" style={{ position: "relative" }}>
                        <PageSectionBlurBackdrop isActive={Boolean(expandedCard)} onDismiss={collapseFromBlurredArea} />
                        {/* Event Info */}
                        <ParentSectionFocusWrap
                            id={NEW_ENTRY_SCROLL.header}
                            card="header"
                            expandedCard={expandedCard}
                            onDismiss={collapseFromBlurredArea}>
                            <EntryPageHeader
                                event={currentEvent}
                                expandedCard={expandedCard}
                                handleCollapseOrExpand={handleCollapseOrExpand}
                                onContinue={collapseExpandedCard}
                                saveRequestKey={saveChangesRequestKey}
                            />
                        </ParentSectionFocusWrap>

                        {/* Horse entries + Add/Copy actions */}
                        <CompactEntryGrid
                            currentEvent={currentEvent}
                            currentEntries={currentEntries}
                            extraNewEntryCount={extraNewEntryCount}
                            copyingEntryCount={copyingEntryCount}
                            expandedCard={expandedCard}
                            pendingEntrySubtitles={pendingEntrySubtitles}
                            savingEntryId={savingEntryId}
                            exitingEntryId={exitingEntryId}
                            isDeleting={isDeleting}
                            entryToDelete={entryToDelete}
                            saveChangesRequestKey={saveChangesRequestKey}
                            isLoading={isLoading}
                            canCopyPreviousEntry={canCopyPreviousEntry}
                            onCollapseOrExpand={handleCollapseOrExpand}
                            onSubmitExisting={saveUpdatedEntry}
                            onSubmitNew={saveInitialEntry}
                            onDeleteSaved={handleDeleteSavedEntry}
                            onDeleteDraft={handleDeleteDraft}
                            onEntryExitComplete={handleEntryExitComplete}
                            onAddAnotherEntry={handleAddAnotherEntry}
                            onOpenCopyEntryModal={handleOpenCopyEntryModal}
                            onUnsavedChangesChange={setHasUnsavedChangesInExpandedCard}
                            onCollapseFromBlurredArea={collapseFromBlurredArea}
                        />

                        {/* Stabling + extras for all completed entries */}
                        <ParentSectionFocusWrap
                            id={NEW_ENTRY_SCROLL.stabling}
                            card="stabling"
                            expandedCard={expandedCard}
                            onDismiss={collapseFromBlurredArea}>
                            <EntrySectionCard
                                event={currentEvent}
                                leadingEmoji="📦"
                                title="Stabling + Extras"
                                subtitle="Stalls, Bedding, Feed + Extras"
                                chipTitle="Not Started"
                                trashIcon={false}
                                expandButtonTitle="Start"
                                isExpand={expandedCard === "stabling"}
                                cardContent="stabling"
                                completedEventEntries={currentEntries || undefined}
                                toggleExpansion={handleCollapseOrExpand("stabling")}
                                onSubmit={collapseExpandedCard}
                                stablingAccordionFocusKey={stablingAccordionFocusKey}
                                stablingExpandOnlyEntryId={stablingExpandOnlyEntryId}
                                onStablingCardCollapse={clearStablingExpandTarget}
                                saveRequestKey={saveChangesRequestKey}
                                onUnsavedChangesChange={setHasUnsavedChangesInExpandedCard}
                            />
                        </ParentSectionFocusWrap>

                        {/* Rules / agreements / waivers */}
                        <ParentSectionFocusWrap
                            id={NEW_ENTRY_SCROLL.waivers}
                            card="waivers"
                            expandedCard={expandedCard}
                            onDismiss={collapseFromBlurredArea}>
                            <EntrySectionCard
                                event={currentEvent}
                                leadingEmoji="📋"
                                title="Waivers"
                                subtitle="Rules, Agreements, Waivers"
                                chipTitle="Not Started"
                                trashIcon={false}
                                expandButtonTitle="Start"
                                isExpand={expandedCard === "waivers"}
                                cardContent="waivers"
                                completedEventEntries={currentEntries || undefined}
                                toggleExpansion={handleCollapseOrExpand("waivers")}
                                onSubmit={collapseExpandedCard}
                                waiversAccordionFocusKey={waiversAccordionFocusKey}
                                waiversExpandOnlyEntryId={waiversExpandOnlyEntryId}
                                onWaiversCardCollapse={clearWaiversExpandTarget}
                                saveRequestKey={saveChangesRequestKey}
                                onUnsavedChangesChange={setHasUnsavedChangesInExpandedCard}
                            />
                        </ParentSectionFocusWrap>

                        {/* Totals review; edit actions scroll back to a section via handleNavigateFromSummary */}
                        <ParentSectionFocusWrap
                            id={NEW_ENTRY_SCROLL.summary}
                            card="summary"
                            expandedCard={expandedCard}
                            onDismiss={collapseFromBlurredArea}>
                            <EntrySectionCard
                                event={currentEvent}
                                leadingEmoji="📊"
                                title="Entry Summary"
                                subtitle="Review totals before submit"
                                chipTitle="Not Started"
                                trashIcon={false}
                                expandButtonTitle="Review"
                                isExpand={expandedCard === "summary"}
                                cardContent="summary"
                                completedEventEntries={currentEntries || undefined}
                                toggleExpansion={handleCollapseOrExpand("summary")}
                                onSubmit={collapseExpandedCard}
                                onNavigateFromSummary={handleNavigateFromSummary}
                                saveRequestKey={saveChangesRequestKey}
                            />
                        </ParentSectionFocusWrap>
                    </IonCol>
                ) : (
                    <p>No event was found.</p>
                )}
            </IonContent>

            <UnsavedChangesModal
                isOpen={showUnsavedChangesModal}
                onClose={handleCloseUnsavedChangesModal}
                onDontSave={handleDismissUnsavedChangesModal}
                onSaveChanges={handleSaveChangesFromModal}
                sectionInfo={expandedSectionInfo}
            />

            {currentEvent && user && (
                <CopyPreviousEntryModal
                    isOpen={showCopyEntryModal}
                    onClose={handleCloseCopyEntryModal}
                    onContinue={handleCopyEntries}
                    currentEvent={currentEvent}
                    currentEventEntries={currentEntries ?? []}
                    personId={user.id}
                />
            )}

            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={cancelDeleteEntry}
                onConfirm={confirmDeleteEntry}
                title="Delete an Entry"
                warningMessage="Deleting an entry is permanent."
                errorMessage={error}
                isLoading={isDeleting}>
                {entryToDelete && (
                    <BasicEntryInfoCard currentEventEntry={entryToDelete} entryNumber={entryToDeleteNumber} />
                )}
            </DeleteConfirmationModal>
        </IonPage>
    );
};

export default EventNewEntryPage;